import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface WhiteLabelConfig {
  logoUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  appName: string;
  primaryColor: string;
  accentColor: string;
  customDomain: string | null;
  domainVerified: boolean;
  emailFromName: string | null;
  customFooterText: string | null;
  hidePoweredBy: boolean;
}

const DEFAULT_BRANDING: WhiteLabelConfig = {
  logoUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
  appName: "CoreGrowth",
  primaryColor: "222 64% 33%",
  accentColor: "160 84% 39%",
  customDomain: null,
  domainVerified: false,
  emailFromName: null,
  customFooterText: null,
  hidePoweredBy: false,
};

interface BrandingContextType {
  branding: WhiteLabelConfig;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType>({
  branding: DEFAULT_BRANDING,
  isLoading: true,
  refresh: async () => {},
});

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    // Safe defaults statt Error werfen
    console.warn("[useBranding] Used outside BrandingProvider, returning safe defaults");
    return {
      branding: DEFAULT_BRANDING,
      isLoading: false,
      refresh: async () => {},
    };
  }
  return context;
}

interface BrandingProviderProps {
  children: ReactNode;
}

export function BrandingProvider({ children }: BrandingProviderProps) {
  const { profile } = useAuth();
  const [branding, setBranding] = useState<WhiteLabelConfig>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);

  const loadBranding = useCallback(async () => {
    // Graceful handling - warten bis Auth geladen ist
    if (!profile?.company_id) {
      setBranding(DEFAULT_BRANDING);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("white_label_config")
        .select("*")
        .eq("company_id", profile.company_id)
        .maybeSingle();

      // Bei JEDEM Fehler auf Default zurückfallen - kein throw!
      if (error) {
        console.warn("[BrandingContext] Fehler beim Laden:", error.message);
        setBranding(DEFAULT_BRANDING);
        setIsLoading(false);
        return;
      }
      
      if (data) {
        const config: WhiteLabelConfig = {
          logoUrl: data.logo_url,
          logoDarkUrl: data.logo_dark_url,
          faviconUrl: data.favicon_url,
          appName: data.app_name || DEFAULT_BRANDING.appName,
          primaryColor: data.primary_color || DEFAULT_BRANDING.primaryColor,
          accentColor: data.accent_color || DEFAULT_BRANDING.accentColor,
          customDomain: data.custom_domain,
          domainVerified: data.domain_verified ?? false,
          emailFromName: data.email_from_name,
          customFooterText: data.custom_footer_text,
          hidePoweredBy: data.hide_powered_by ?? false,
        };
        setBranding(config);

        // Apply CSS custom properties for colors
        if (data.primary_color) {
          document.documentElement.style.setProperty("--primary", data.primary_color);
        }
        if (data.accent_color) {
          document.documentElement.style.setProperty("--accent", data.accent_color);
        }

        // Update favicon if set
        if (data.favicon_url) {
          const faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
          if (faviconLink) {
            faviconLink.href = data.favicon_url;
          }
        }

        // Update document title
        if (data.app_name) {
          document.title = data.app_name;
        }
      } else {
        setBranding(DEFAULT_BRANDING);
      }
    } catch (err) {
      console.error("Error loading branding:", err);
      setBranding(DEFAULT_BRANDING);
    } finally {
      setIsLoading(false);
    }
  }, [profile?.company_id]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await loadBranding();
  }, [loadBranding]);

  return (
    <BrandingContext.Provider value={{ branding, isLoading, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}
